import { type FC, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useRejectedProducts } from '../../hooks/useSyncRuns';
import { ErrorBanner, Panel, Table } from './syncUi';
import { formatDateTime } from './syncFormat';

const REASON_HELP: Record<string, string> = {
    MISSING_PRODUCT_ID: 'Arch sent a row with no usable ProductId, so there was nothing to match on.',
    INVALID_STOCK: 'StockOnHand was missing or not a whole number.',
    DUPLICATE_PRODUCT_ID: 'The same product appeared twice in one batch. The first was kept.',
    MISSING_SKU: 'The row had neither a Sku nor a ProductCode.',
    MISSING_NAME: 'The product had no name.',
    UNKNOWN_DEPARTMENT: 'The department is not in our mapping, so the product went to Other.',
};

/**
 * Rows a sync could not apply, kept with their raw payload so they can be chased.
 *
 * Only malformed rows land here. Products we simply do not hold are counted on the
 * run instead — one row each would be tens of thousands every fifteen minutes, and
 * a backlog is not per-row news.
 */
const RejectedProductsPage: FC = () => {
    const { rejections, loading, error, fetchRejections } = useRejectedProducts();
    const [reason, setReason] = useState<string>('all');
    const [expanded, setExpanded] = useState<string | null>(null);

    useEffect(() => {
        void fetchRejections();
    }, [fetchRejections]);

    const reasons = useMemo(() => {
        const counts = new Map<string, number>();
        for (const r of rejections) counts.set(r.reason, (counts.get(r.reason) ?? 0) + 1);
        return [...counts.entries()].sort((a, b) => b[1] - a[1]);
    }, [rejections]);

    const visible = reason === 'all' ? rejections : rejections.filter((r) => r.reason === reason);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-semibold text-stone-900">Rejected Products</h2>
                    <p className="mt-1 text-sm text-stone-500">
                        Rows the product and stock syncs could not apply, with the raw payload Arch sent.
                        A rejection never fails a run — the rest of the batch is written regardless.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => void fetchRejections()}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {error && <ErrorBanner message={error} />}

            {reasons.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setReason('all')}
                        className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                            reason === 'all'
                                ? 'bg-stone-900 text-white border-stone-900'
                                : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
                        }`}
                    >
                        All ({rejections.length})
                    </button>
                    {reasons.map(([name, count]) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => setReason(name)}
                            title={REASON_HELP[name] ?? ''}
                            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                                reason === name
                                    ? 'bg-stone-900 text-white border-stone-900'
                                    : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50'
                            }`}
                        >
                            {name} ({count})
                        </button>
                    ))}
                </div>
            )}

            {reason !== 'all' && REASON_HELP[reason] && (
                <p className="text-sm text-stone-500">{REASON_HELP[reason]}</p>
            )}

            <Panel title="Rejections" description="Newest first. Click a row to see the payload Arch sent.">
                <Table headers={['When', 'Reason', 'Arch product id', 'Code / SKU', 'Detail']}>
                    {loading && rejections.length === 0 ? (
                        <tr><td colSpan={5} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                    ) : visible.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="py-10 px-6 text-stone-400">
                                Nothing rejected. That is the good outcome.
                            </td>
                        </tr>
                    ) : (
                        visible.map((row) => (
                            <>
                                <tr
                                    key={row.id}
                                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                                    className="hover:bg-stone-50/50 transition-colors cursor-pointer"
                                >
                                    <td className="py-4 px-6 text-stone-600 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                                    <td className="py-4 px-6">
                                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                                            {row.reason}
                                        </span>
                                    </td>
                                    <td className="py-4 px-6 text-stone-900">{row.external_product_id ?? '—'}</td>
                                    <td className="py-4 px-6 text-stone-600">{row.sku ?? '—'}</td>
                                    <td className="py-4 px-6 text-stone-500 max-w-md truncate" title={row.details ?? ''}>
                                        {row.details ?? '—'}
                                    </td>
                                </tr>
                                {expanded === row.id && (
                                    <tr key={`${row.id}-payload`} className="bg-stone-50/60">
                                        <td colSpan={5} className="px-6 py-4">
                                            <pre className="max-h-72 overflow-auto rounded-xl bg-stone-900 p-4 text-xs text-stone-100">
                                                {JSON.stringify(row.payload, null, 2)}
                                            </pre>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))
                    )}
                </Table>
            </Panel>
        </div>
    );
};

export default RejectedProductsPage;
